import { CopyIconButton, SelectDropDown, Toggle } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FormattedMessage, injectIntl, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import { createHubTask, pollTaskStatus } from '../../../../../crud/integrationHub.crud'
import Spinner from '../../../../../partials/content/Spinner'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../../../util/table.util'
import timeZones from '../../../../../util/timezones'
import { ACTIVATION_DURATION_DEFAULT_MESSAGE } from '../../messageDefaults'
import TemporaryAccessPassSuccessItem from './TemporaryAccessPassSuccessItem'

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onUpdate: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.array,
}

const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

const findTimeZone = () => {
  const browserTimeZone = getBrowserTimeZone()
  const timeZone = timeZones.find((tz) => tz.utc.includes(browserTimeZone))
  return { label: timeZone.text, value: timeZone.offset }
}

function TemporaryAccessPass(props) {
  const { show = false, onHide = () => {}, onUpdate = () => {}, employees = [] } = props ?? {}

  const defaultActivationDuration = 1 // hours

  const [taskResponses, setTaskResponses] = useState({})
  const [taskErrors, setTaskErrors] = useState({})
  const [delayedStartTime, setDelayedStartTime] = useState(false)

  const steps = Array.from({ length: 8 }, (_, i) => i + 1)
  const [activationDuration, setActivationDuration] = useState(steps[defaultActivationDuration - 1])
  const [oneTimeUse, setOneTimeUse] = useState(false)

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  const intl = useIntl()

  const STATUS = {
    REVIEW: 'REVIEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  }

  const ERRORS = {
    GLOBAL_ERRORS: 'GLOBAL_ERRORS',
    OPERATIONAL: 'OPERATIONAL',
  }

  const [status, setStatus] = useState(STATUS.REVIEW)

  const initDate = new Date()
  const [date, setDate] = useState(initDate)
  const [hour, setHour] = useState(0)
  const [minute, setMinute] = useState(0)
  const [meridiem, setMeridiem] = useState('AM')
  const [timeZone, setTimeZone] = useState(findTimeZone())

  const handleDelayedStartTimeChange = () => {
    setDelayedStartTime(!delayedStartTime)
    const now = new Date()
    setDate(now)
    setHour(now.getHours() % 12 || 12)
    setMinute(now.getMinutes())
    setMeridiem(now.getHours() >= 12 ? 'PM' : 'AM')
    setTimeZone(findTimeZone())
  }

  const handleActivationDurationChange = (e) => {
    const index = Number.parseInt(e.target.value, 10)
    setActivationDuration(steps[index])
  }

  const handleOneTimeUseChange = () => {
    setOneTimeUse(!oneTimeUse)
  }

  const handleMeridiemChange = (value) => {
    setMeridiem(value)
  }

  const handleDateChange = (selectedDate) => {
    const utcDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()))
    setDate(utcDate)
  }

  const getUTCDateTime = () => {
    const selectedDate = new Date(date)

    let adjustedHour = hour % 12
    if (meridiem === 'PM') adjustedHour += 12

    selectedDate.setUTCHours(adjustedHour, minute, 0, 0)

    const utc = new Date(selectedDate.getTime() - Number.parseInt((timeZone?.value ?? 0) * 60 * 60 * 1000, 10))

    return utc.toISOString() // ISO 8601 w UTC
  }

  const timeZonesOptions = useMemo(
    () =>
      timeZones.map((tz) => ({
        label: tz.text,
        value: tz.offset,
      })),
    [],
  )

  const onApply = async () => {
    setStatus(STATUS.IN_PROGRESS)

    // eslint-disable-next-line no-unused-vars
    const parameters = {
      actions: {
        employeeIdentifiers: employees?.map((emp) => emp.employeeIdentifier || emp.employee_identifier),
        startDateTime: delayedStartTime ? getUTCDateTime() : null,
        isUsableOnce: oneTimeUse,
        lifetimeInMinutes: activationDuration * 60,
      },
      tasktype: TASK_TYPES.temporaryAccessPass,
      action: TASK_ACTIONS.create,
      customerId: selectedCustomer?.id,
      userId: currentUser?.id,
      organizationId: selectedCustomer?.organization,
    }

    try {
      const { data: { taskId } = {} } = await createHubTask(parameters)

      if (taskId) {
        const { data: { _source: task } = {} } =
          (await pollTaskStatus(taskId, {
            retries: 10,
            timeout: 3000,
          })) ?? {}

        setTaskResponses(JSON.parse(task?.responses || '{}'))
        setTaskErrors(JSON.parse(task?.errors || '{}'))
        setStatus(STATUS.COMPLETED)

        await onUpdate()
      }
    } catch (error) {
      setTaskErrors({
        OPERATIONAL: { message: error.message },
      })
      setStatus(STATUS.COMPLETED)
    }

    setTimeout(() => {
      setStatus(STATUS.COMPLETED)
    }, 2000)
  }

  return (
    <Modal show={show} size="lg" className={`right ${clsx({ in: show })}`} backdrop onHide={onHide}>
      <Modal.Header closeButton className="grey-bg-color-1">
        <Modal.Title id="contained-modal-title-vcenter">
          {[STATUS.REVIEW].includes(status) && <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_CREATE_TITLE" />}
          {[STATUS.IN_PROGRESS].includes(status) && <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_IN_PROGRESS" />}
          {[STATUS.COMPLETED].includes(status) && <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_HAS_COMPLETED" />}
        </Modal.Title>

        {employees?.length === 1 && (
          <p className="mt-2">
            {employees?.[0]?.firstName} {employees?.[0]?.lastName}
          </p>
        )}

        <p className="mt-2">
          {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
            ? 'Microsoft 365'
            : selectedIntegration?.IntegrationApplication?.display_name}
        </p>
      </Modal.Header>
      <Modal.Body className="modal-body-sidebar">
        {[STATUS.REVIEW].includes(status) && (
          <>
            <Row className="px-2">
              <Col>
                <p className="text-bold">
                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_SETTINGS" />
                </p>
              </Col>
            </Row>

            <Row className="px-2 mb-2">
              <Col>
                <p>
                  <FormattedMessage
                    id="ENGAGE.TEMPORARY_ACCESS_PASS_CREATE_DESC"
                    values={{ s: employees?.length > 1 ? 's' : '' }}
                  />
                </p>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <div className="text-color-default">
                  <Form.Check
                    label={<FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_DELAYED_START_TIME" />}
                    custom
                    name="delayed-start-time"
                    id="delayed-start-time-checkbox"
                    checked={delayedStartTime}
                    onChange={handleDelayedStartTimeChange}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-2" style={{ display: delayedStartTime ? 'flex' : 'none' }}>
              <Col>
                <div className="ms-4 p-3 rounded" style={{ maxWidth: '400px' }}>
                  {/* Date Picker */}
                  <div className="mb-3">
                    <DatePicker
                      showIcon
                      toggleCalendarOnIconClick
                      icon={
                        <i
                          className="fa fa-calendar"
                          style={{ marginTop: '5px', marginLeft: '2px', color: 'var(--aug-product-blue)' }}
                        />
                      }
                      selected={date}
                      onChange={handleDateChange}
                      dateFormat="MMMM d, yyyy"
                      className="form-control"
                      showPopperArrow={false}
                      // startDate={date}
                      minDate={new Date()}
                      utcOffset={0}
                    />
                  </div>

                  {/* Time Selection */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="text-center me-2">
                      <span className="d-block span mb-1">Hour</span>
                      <input
                        type="number"
                        className="form-control text-center"
                        id="hour-input"
                        value={hour}
                        onChange={(e) => {
                          setHour(Math.max(1, Math.min(12, Number.parseInt(e.target.value, 10) || 9)))
                        }}
                        min="1"
                        max="12"
                        style={{ width: '60px' }}
                      />
                    </div>

                    <div className="text-center">
                      <span className="d-block mb-1">Minute</span>
                      <input
                        type="number"
                        className="form-control text-center"
                        id="minute-input"
                        value={minute.toString().padStart(2, '0')}
                        onChange={(e) => setMinute(Math.max(0, Math.min(59, Number.parseInt(e.target.value, 10) || 0)))}
                        min="0"
                        max="59"
                        style={{ width: '60px' }}
                      />
                    </div>

                    <div className="text-center ms-2" style={{ marginTop: '1.8rem' }}>
                      <div className="btn-group">
                        <button
                          type="button"
                          className={`btn ${meridiem === 'AM' ? 'btn-primary' : 'btn-light'} btn-sm`}
                          onClick={() => handleMeridiemChange('AM')}
                          style={{ padding: '0.2rem 1rem' }}
                        >
                          AM
                        </button>
                        <button
                          type="button"
                          className={`btn ${meridiem === 'PM' ? 'btn-primary' : 'btn-light'} btn-sm`}
                          onClick={() => handleMeridiemChange('PM')}
                          style={{ padding: '0.2rem 1rem' }}
                        >
                          PM
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Timezone Selection */}
                  <div>
                    <SelectDropDown
                      id="is-trusted-dropdown"
                      isClearable={false}
                      value={timeZonesOptions.find((option) => option.label === timeZone.label) ?? null}
                      options={timeZonesOptions}
                      onChange={(option) => setTimeZone(option)}
                    />
                  </div>

                  {/* <div className="mt-3">
                   <span className="d-block ps-2 bg-light text-bold">{formatDateTimeManual(getUTCDateTime())}</span>
                  </div> */}
                </div>
              </Col>
            </Row>

            <Row className="px-3">
              <Col>
                <p className="text-bold">
                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_ACTIVATION_DURATION" />
                </p>
              </Col>
            </Row>

            <Row className="px-3 mb-4">
              <Col sm="12" md="12" lg="12">
                <div className="d-flex align-items-center">
                  <input
                    type="range"
                    className="custom-range"
                    min="0"
                    max={steps.length - 1}
                    step="1"
                    value={steps.indexOf(activationDuration)}
                    onChange={handleActivationDurationChange}
                    style={{ flex: 1 }}
                  />

                  <div>
                    <span className="px-2">
                      {intl.formatMessage(
                        {
                          id: 'ENGAGE.TEMPORARY_ACCESS_PASS_HOURS',
                          defaultMessage: ACTIVATION_DURATION_DEFAULT_MESSAGE,
                        },
                        { count: activationDuration },
                      )}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="px-3 mb-4">
              <Col>
                <div className="d-flex">
                  <Toggle
                    id="toggleJsonView"
                    labelTextStyle="font-size-default text-strong me-3 text-color-default"
                    labelText={intl.formatMessage({ id: 'ENGAGE.TEMPORARY_ACCESS_PASS_ONE_TIME_USE' })}
                    checked={oneTimeUse}
                    onChange={handleOneTimeUseChange}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md="12">
                <p className="font-bolder mb-1 mt-4">
                  <FormattedMessage
                    id="ENGAGE.TEMPORARY_ACCESS_APPLY_PHRASE"
                    values={{ s: employees?.length > 1 ? 's' : '' }}
                  />
                </p>

                <ul className="font-bolder">
                  {employees?.map((employee) => (
                    <li key={employee.id}>{getEmployeeFullName(employee)}</li>
                  ))}
                </ul>
              </Col>
            </Row>
          </>
        )}

        {[STATUS.IN_PROGRESS].includes(status) && (
          <div className="modal-loading-centered">
            <Spinner size="small" />
            <div className="mt-4 text-strong text-color-secondary">
              <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_IN_PROGRESS" />
            </div>
          </div>
        )}

        {!taskErrors[ERRORS.GLOBAL_ERRORS] &&
          !taskErrors[ERRORS.OPERATIONAL] &&
          [STATUS.COMPLETED].includes(status) && (
            <ul className="ps-0">
              {employees?.map((employee) => {
                const employeeId = employee?.employeeIdentifier || employee?.employee_identifier
                const hubTaskResponse = taskResponses?.[employeeId] || null
                const hasError = !!taskErrors[employeeId]

                return (
                  <li key={employee.id || employeeId} className="list-unstyled mt-0">
                    {hasError ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
                        {getEmployeeFullName(employee)}
                      </div>
                    ) : (
                      <TemporaryAccessPassSuccessItem
                        displayName={getEmployeeFullName(employee)}
                        hasDetails={!!hubTaskResponse}
                      >
                        {hubTaskResponse && (
                          <>
                            <h5 className="px-2 mb-2 text-bold">
                              <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS" />
                            </h5>

                            <div className="px-4 ms-2">
                              <div>
                                <strong>
                                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_ACCOUNT" />
                                </strong>{' '}
                                {employee?.email}
                              </div>

                              <div>
                                <strong>
                                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_VALID_FROM" />
                                </strong>{' '}
                                {moment(hubTaskResponse?.startDateTime).format('MMMM Do YYYY [at] h:mm:ss a')}
                              </div>

                              <div>
                                <strong>
                                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_VALID_UNTIL" />
                                </strong>{' '}
                                {moment(hubTaskResponse?.startDateTime)
                                  .add(hubTaskResponse?.lifetimeInMinutes, 'minutes')
                                  .format('MMMM Do YYYY [at] h:mm:ss a')}
                              </div>

                              <div>
                                <strong>
                                  <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_CREATED" />
                                </strong>{' '}
                                {moment(hubTaskResponse?.createdDateTime).format('MMMM Do YYYY [at] h:mm:ss a')}
                              </div>
                            </div>

                            <hr />

                            <h4 className="px-2 mb-4">
                              <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_DETAILS" />
                            </h4>

                            <div className="px-4 ms-2 mb-4">
                              <h5>
                                <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_PROVIDE_PASS" />
                              </h5>
                              <p>
                                <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_PROVIDE_PASS_DESC" />
                              </p>
                              <div
                                className="ps-2 pe-2 border rounded d-flex space-between align-items-center mb-4"
                                style={{ backgroundColor: 'var(--bs-secondary-bg)' }}
                              >
                                {hubTaskResponse?.temporaryAccessPass}
                                <CopyIconButton value={hubTaskResponse?.temporaryAccessPass || ''} />
                              </div>
                              <h5 className="mt-3">
                                <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_SECURE_REGISTRATION" />
                              </h5>
                              <p>
                                <FormattedMessage id="ENGAGE.TEMPORARY_ACCESS_PASS_SECURE_REGISTRATION_DESC" />
                              </p>
                              <div
                                className="ps-2 pe-2 border rounded d-flex space-between align-items-center"
                                style={{ backgroundColor: 'var(--bs-secondary-bg)' }}
                              >
                                https://aka.ms/mysecurityinfo
                                <CopyIconButton value="https://aka.ms/mysecurityinfo" />
                              </div>
                            </div>

                            <hr />
                          </>
                        )}
                      </TemporaryAccessPassSuccessItem>
                    )}

                    {hasError && (
                      <div style={{ width: '100%' }} className="font-color-danger">
                        <pre
                          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
                          className="font-color-danger"
                        >
                          {JSON.stringify(taskErrors[employeeId] || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

        {taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
          </div>
        )}

        {taskErrors[ERRORS.GLOBAL_ERRORS] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
            <pre
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
              className="font-color-danger mt-1"
            >
              {JSON.stringify(taskErrors[ERRORS.GLOBAL_ERRORS] || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={clsx('d-flex', 'justify-content-between')}>
        <Button onClick={onHide} variant="secondary">
          <FormattedMessage id={[STATUS.REVIEW].includes(status) ? 'GENERAL.CANCEL' : 'GENERAL.CLOSE'} />
        </Button>
        {[STATUS.REVIEW].includes(status) && (
          <Button
            id="temporary-access-pass-submit-btn"
            variant="primary"
            onClick={onApply}
            className="bg-color-engage pull-right"
          >
            <FormattedMessage id="GENERAL.APPLY" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

TemporaryAccessPass.propTypes = propTypes

export default injectIntl(TemporaryAccessPass)
